import React, { createContext, useCallback, useContext } from 'react';
import { axiosTimeout } from '../helpers/axiosTimeout';
import api from '../services/api';
import { useApp } from './Global';

const IntegrationContext = createContext();

const IntegrationProvider = ({ children }) => {
  const { searchUser } = useApp();

  const verifyUserRestriction = useCallback(async ({ document }) => {
    const token = localStorage.getItem('@Braspress:token');

    const response = await api.get(
      `${process.env.REACT_APP_URL_MS_INTEGRATION}/integration/document-restriction`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          Document: document
        },
        timeout: axiosTimeout
      }
    );

    const { data } = response.data;

    return data;
  }, []);

  const getAddressByZipPostalCode = useCallback(async ({ zipPostalCode }) => {
    try {
      const token = localStorage.getItem('@Braspress:token');

      const response = await api.get(
        `${process.env.REACT_APP_URL_MS_INTEGRATION}/integration/address`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          params: {
            Cep: zipPostalCode
          },
          timeout: axiosTimeout
        }
      );

      const { data } = response.data;

      return data;
    } catch (error) {
      return error;
    }
  }, []);

  const getHolidaysByZipPostalCode = useCallback(async ({ zipPostalCode }) => {
    try {
      const token = localStorage.getItem('@Braspress:token');

      const response = await api.get(
        `${process.env.REACT_APP_URL_MS_INTEGRATION}/integration/holidays-for-address`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          params: {
            pzipcode: zipPostalCode
          },
          timeout: axiosTimeout
        }
      );

      return response;
    } catch (error) {
      return error;
    }
  }, []);

  const searchKnowledge = useCallback(async ({ params }) => {
    try {
      const token = localStorage.getItem('@Braspress:token');

      const response = await api.get(
        `${process.env.REACT_APP_URL_MS_INTEGRATION}/integration/search-knowledge`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          params,
          timeout: axiosTimeout
        }
      );

      if (response.status === 200) {
        const { data } = response.data;

        return data;
      }

      return { list: [] };
    } catch (error) {
      return error;
    }
  }, []);

  const searchInvoicesByKnowledgeId = useCallback(async ({ params }) => {
    try {
      const token = localStorage.getItem('@Braspress:token');

      const response = await api.get(
        `${process.env.REACT_APP_URL_MS_INTEGRATION}/integration/knowledgeInvoice`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          params,
          timeout: axiosTimeout
        }
      );

      if (response.status === 200) {
        const { list } = response.data.data;

        return list;
      }

      return [];
    } catch (error) {
      return error;
    }
  }, []);

  const getBranchList = useCallback(async () => {
    try {
      const token = localStorage.getItem('@Braspress:token');

      const response = await api.get(
        `${process.env.REACT_APP_URL_MS_INTEGRATION}/integration/branchs`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          timeout: axiosTimeout
        }
      );

      const branchList = response?.data.data;

      if (branchList) {
        branchList.sort((a, b) => {
          if (a.initials < b.initials) {
            return -1;
          }
          if (a.initials > b.initials) {
            return 1;
          }
          return 0;
        });

        return branchList;
      }

      return [];
    } catch (error) {
      return error;
    }
  }, []);

  const getBranchDetails = useCallback(async ({ params }) => {
    try {
      const token = localStorage.getItem('@Braspress:token');

      params.Type_Route = 'C';

      const response = await api.get(
        `${process.env.REACT_APP_URL_MS_INTEGRATION}/integration/branch-details`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          params,
          timeout: axiosTimeout
        }
      );

      if (response.status === 200) {
        const list = response.data.data;

        return list;
      }

      return [];
    } catch (error) {
      return error;
    }
  }, []);

  const contextValues = {
    searchUser,
    verifyUserRestriction,
    getAddressByZipPostalCode,
    getHolidaysByZipPostalCode,
    searchKnowledge,
    searchInvoicesByKnowledgeId,
    getBranchList,
    getBranchDetails
  };

  return (
    <IntegrationContext.Provider value={contextValues}>
      {children}
    </IntegrationContext.Provider>
  );
};

function useIntegration() {
  const context = useContext(IntegrationContext);

  if (!context) {
    throw new Error(
      'useIntegration must be used within an IntegrationProvider'
    );
  }

  return context;
}

export { IntegrationProvider, useIntegration };
